import type { App } from 'vue'

let vueformLoaded = false
let loadPromise: Promise<void> | null = null
let appInstance: App | null = null

// Called once from main.ts after createApp() so the router guard can reference it.
export function setVueApp(app: App): void {
    appInstance = app
}

// Lazily load Vueform and (where applicable) the Builder, then register them
// on the Vue app instance. Safe to call multiple times — loads only once.
// Can be called without an argument after setVueApp() has been called.
export async function loadVueformConditionally(app?: App): Promise<void> {
    const targetApp = app ?? appInstance
    if (!targetApp) {
        console.warn('loadVueformConditionally called before app instance was set')
        return
    }
    if (vueformLoaded) return
    if (loadPromise) return loadPromise

    loadPromise = (async () => {
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        const isStage = window.location.hostname.includes('appv5.stage-ovationcxm.app')
        const isPreprod = window.location.hostname.includes('appv5.preprod-ovationcxm.app')
        const isProd =
            window.location.hostname.includes('app.ovationcxm.com') ||
            window.location.hostname.includes('appv5.ovationcxm.app')

        try {
            if (isLocalhost || isStage || isPreprod || isProd) {
                const [{ default: Vueform }, { default: vueformConfig }, { default: Builder }, { default: builderConfig }] =
                    await Promise.all([
                        import('@vueform/vueform'),
                        import('../vueform-with-builder.config'),
                        import('@vueform/builder'),
                        import('../builder.config'),
                    ])
                targetApp.use(Vueform, vueformConfig)
                targetApp.use(Builder, builderConfig)
            } else {
                const [{ default: Vueform }, { default: vueformConfig }] = await Promise.all([
                    import('@vueform/vueform'),
                    import('../vueform-no-builder.config'),
                ])
                targetApp.use(Vueform, vueformConfig)
            }
            vueformLoaded = true
        } catch (error) {
            console.warn('Failed to load Vueform packages:', error)
            loadPromise = null // allow retry on next navigation
        }
    })()

    return loadPromise
}
